import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import type { NewsItem } from '../types/feed';
import type { EvaluationResult } from '../services/postGenerator';
import { renderForPlatform } from '../utils/formatters';

export type Platform = 'whatsapp' | 'linkedin' | 'twitter';
export type PostLength = 'brief' | 'standard' | 'detailed';
export type PostLanguage = 'en' | 'he' | 'es' | 'fr' | 'de' | 'ar';

const LANGUAGE_KEY = 'copywriter_last_language'

function loadLanguage(): PostLanguage {
  try {
    const v = localStorage.getItem(LANGUAGE_KEY) as PostLanguage | null
    return v ?? 'en'
  } catch {
    return 'en'
  }
}

function saveLanguage(lang: PostLanguage) {
  try {
    localStorage.setItem(LANGUAGE_KEY, lang)
  } catch { /* ignore */ }
}

interface PostState {
  selectedItem: NewsItem | null;
  content: string;
  platform: Platform;
  tone: number; // 0-100, 0=technical, 100=marketing
  length: PostLength;
  language: PostLanguage;
  thinkingBudget: number; // 0=off, 1024/2048/4096
  isGenerating: boolean;
  scorecard: EvaluationResult | null;
  iterations: number;
  generationStatus: 'idle' | 'generating' | 'evaluating' | 'refining';
  refinementIteration: number;
}

interface PostContextValue extends PostState {
  setSelectedItem: (item: NewsItem | null) => void;
  setContent: (content: string) => void;
  setPlatform: (platform: Platform) => void;
  setTone: (tone: number) => void;
  setLength: (length: PostLength) => void;
  setLanguage: (language: PostLanguage) => void;
  setThinkingBudget: (budget: number) => void;
  regenerate: () => Promise<void>;
  copyToClipboard: () => Promise<void>;
  shareToLinkedIn: () => void;
  saveDraft: () => void;
  /** Restore settings from a draft — best effort, unknown keys silently skipped */
  restoreFromDraftSettings: (settings: Record<string, unknown>) => void;
  reset: () => void;
  /** External save trigger so DraftContext can call it */
  onSaveRequest: ((fn: () => void) => void) | null;
}

const PostContext = createContext<PostContextValue | undefined>(undefined);

const initialState: PostState = {
  selectedItem: null,
  content: '',
  platform: 'linkedin',
  tone: 50,
  length: 'standard',
  language: loadLanguage(),
  thinkingBudget: 1024,
  isGenerating: false,
  scorecard: null,
  iterations: 1,
  generationStatus: 'idle',
  refinementIteration: 0,
};

const VALID_PLATFORMS = new Set<Platform>(['whatsapp', 'linkedin', 'twitter'])
const VALID_LENGTHS = new Set<PostLength>(['brief', 'standard', 'detailed'])
const VALID_LANGUAGES = new Set<PostLanguage>(['en', 'he', 'es', 'fr', 'de', 'ar'])

export function PostProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PostState>(initialState);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Callback ref so EditorPanel can wire up the draft-save trigger
  const saveRequestRef = useRef<(() => void) | null>(null)

  const setSelectedItem = useCallback((item: NewsItem | null) => {
    setState((prev) => ({ ...prev, selectedItem: item }));
  }, []);

  const setContent = useCallback((content: string) => {
    setState((prev) => ({ ...prev, content }));
  }, []);

  const setPlatform = useCallback((platform: Platform) => {
    setState((prev) => ({ ...prev, platform }));
  }, []);

  const setTone = useCallback((tone: number) => {
    setState((prev) => ({ ...prev, tone }));
  }, []);

  const setLength = useCallback((length: PostLength) => {
    setState((prev) => ({ ...prev, length }));
  }, []);

  const setLanguage = useCallback((language: PostLanguage) => {
    saveLanguage(language);
    setState((prev) => ({ ...prev, language }));
  }, []);

  const setThinkingBudget = useCallback((thinkingBudget: number) => {
    setState((prev) => ({ ...prev, thinkingBudget }));
  }, []);

  /** Best-effort settings restore from draft metadata */
  const restoreFromDraftSettings = useCallback((settings: Record<string, unknown>) => {
    setState(prev => {
      const next = { ...prev }
      try {
        if (typeof settings.platform === 'string' && VALID_PLATFORMS.has(settings.platform as Platform)) {
          next.platform = settings.platform as Platform
        }
      } catch { /* skip */ }
      try {
        if (typeof settings.tone === 'number' && settings.tone >= 0 && settings.tone <= 100) {
          next.tone = settings.tone
        }
      } catch { /* skip */ }
      try {
        if (typeof settings.length === 'string' && VALID_LENGTHS.has(settings.length as PostLength)) {
          next.length = settings.length as PostLength
        }
      } catch { /* skip */ }
      try {
        if (typeof settings.language === 'string' && VALID_LANGUAGES.has(settings.language as PostLanguage)) {
          next.language = settings.language as PostLanguage
          saveLanguage(settings.language as PostLanguage)
        }
      } catch { /* skip */ }
      return next
    })
  }, []);

  const regenerate = useCallback(async () => {
    await Promise.resolve();
    const current = stateRef.current;
    if (!current.selectedItem) return;

    setState((prev) => ({
      ...prev,
      isGenerating: true,
      scorecard: null,
      iterations: 1,
      generationStatus: 'generating',
      refinementIteration: 0,
    }));

    try {
      const { generatePostAsync } = await import('../services/postGenerator');
      const result = await generatePostAsync({
        newsItem: current.selectedItem,
        platform: current.platform,
        tone: current.tone,
        length: current.length,
        language: current.language,
        thinkingBudget: current.thinkingBudget,
      });

      setState((prev) => ({
        ...prev,
        content: result.post_text,
        scorecard: result.scorecard,
        iterations: result.iterations,
        isGenerating: false,
        generationStatus: 'idle',
        refinementIteration: 0,
      }));

      if (!result.scorecard && result._pollScorecard) {
        result._pollScorecard().then((scorecard) => {
          if (scorecard) setState((prev) => ({ ...prev, scorecard }));
        });
      }

      // Auto-save draft after regeneration
      saveRequestRef.current?.()
    } catch (error) {
      console.error('Failed to generate post:', error);
      setState((prev) => ({ ...prev, isGenerating: false, generationStatus: 'idle' }));
    }
  }, []);

  const saveDraft = useCallback(() => {
    saveRequestRef.current?.()
  }, []);

  const copyToClipboard = useCallback(async () => {
    if (!state.content) return;
    try {
      // Render platform-specific encoding (e.g. **bold** → Unicode for LinkedIn)
      const rendered = renderForPlatform(state.content, state.platform);
      await navigator.clipboard.writeText(rendered);
      saveRequestRef.current?.()
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }, [state.content, state.platform]);

  const shareToLinkedIn = useCallback(() => {
    if (!state.content || !state.selectedItem) return;
    const { buildLinkedInShareUrl } = require('../utils/shareUrls');
    // Render Unicode bold/italic before sharing
    const rendered = renderForPlatform(state.content, state.platform);
    const url = buildLinkedInShareUrl(rendered, state.selectedItem.source_url);
    window.open(url, '_blank', 'noopener,noreferrer');
    saveRequestRef.current?.()
  }, [state.content, state.platform, state.selectedItem]);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const value: PostContextValue = {
    ...state,
    setSelectedItem,
    setContent,
    setPlatform,
    setTone,
    setLength,
    setLanguage,
    setThinkingBudget,
    regenerate,
    copyToClipboard,
    shareToLinkedIn,
    saveDraft,
    restoreFromDraftSettings,
    reset,
    onSaveRequest: (fn) => { saveRequestRef.current = fn },
  };

  return <PostContext.Provider value={value}>{children}</PostContext.Provider>;
}

export function usePost() {
  const context = useContext(PostContext);
  if (context === undefined) {
    throw new Error('usePost must be used within a PostProvider');
  }
  return context;
}
